import { BRAND, SITE_ORIGIN } from "../brand";
import { LegalPage } from "../components/marketing-page";
import { marketingLinks, socialImageMeta } from "../components/marketing/layout";

export const links = marketingLinks;

export const meta = () => [
  { title: `Terms of Service — ${BRAND.name}` },
  {
    name: "description",
    content: `Terms of service for ${BRAND.name}, a Shopify app by Legitimate LLC. Governs your use of the ${BRAND.name} FDA Nutrition Facts label generation tool.`,
  },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/terms` },
  { property: "og:url", content: `${SITE_ORIGIN}/terms` },
  { property: "og:type", content: "website" },
  ...socialImageMeta(),
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `Terms of Service — ${BRAND.name}`,
      url: `${SITE_ORIGIN}/terms`,
      publisher: { "@type": "Organization", name: "Legitimate LLC" },
    },
  },
];

export default function Terms() {
  return (
    <LegalPage title={`Terms of Service — ${BRAND.name}`}>
      {/* ACCEPTANCE */}
      <h2>Acceptance of terms</h2>
      <p>
        By installing, accessing, or using {BRAND.name} (the "Service"), you
        agree to be bound by these Terms of Service ("Terms"). If you are
        accepting on behalf of a business, you represent that you have the
        authority to bind that business to these Terms. If you do not agree,
        do not install or use the Service.
      </p>
      <p>
        These Terms form a binding agreement between you (the "Merchant") and
        Legitimate LLC ("Company", "we", "us", "our"), the publisher of{" "}
        {BRAND.name}.
      </p>

      {/* SERVICE DESCRIPTION */}
      <h2>Service description</h2>
      <p>
        {BRAND.name} is a Shopify embedded app that helps food and beverage
        merchants generate US Nutrition Facts label panels. It works as
        follows:
      </p>
      <ul>
        <li>
          You select a product from your Shopify store and enter nutrition
          values (serving size, serving count, and per-serving nutrient
          amounts).
        </li>
        <li>
          The Service applies the rounding and formatting rules published in{" "}
          <strong>FDA 21 CFR 101.9</strong> to the values you provide and
          renders a live Nutrition Facts panel preview inside the Shopify
          admin.
        </li>
        <li>
          You can save the calculated label to your store (stored as a Shopify
          metaobject), browse your label history, and export the label as a
          print-ready image.
        </li>
      </ul>

      {/* DISCLAIMER — OWN SECTION, PROMINENT. The id is the anchor the support
          page's FAQ links to (/terms#disclaimer). */}
      <div className="nm-disclaimer" id="disclaimer">
        <h2>Important — Calculation Disclaimer</h2>
        <p>
          {BRAND.name} is a <em>calculation and formatting tool</em>. It
          applies published FDA rounding rules to the nutrition values{" "}
          <em>you</em> provide. It is <strong>NOT</strong> legal, nutritional,
          or regulatory advice, and it does <strong>NOT</strong> guarantee that
          any label produced is compliant, accurate, or suitable for your
          specific product, packaging, or market.
        </p>
        <p>
          <strong>
            You are solely responsible for the accuracy of the data you enter
            and for verifying the final label — including values, allergen
            statements, nutrient claims, serving size declarations, and
            formatting — against current FDA requirements (21 CFR Part 101)
            before printing, publishing, listing for sale, or using it on
            product packaging in any way.
          </strong>
        </p>
        <p>
          Always confirm your final label with a qualified regulatory
          professional or registered dietitian before commercial use.
          Regulatory requirements change; the Service may not reflect the
          most recent guidance at all times.
        </p>
      </div>

      {/* ACCEPTABLE USE */}
      <h2>Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use the Service for any unlawful purpose or in violation of any
          applicable law or regulation.
        </li>
        <li>
          Attempt to reverse-engineer, decompile, disassemble, or otherwise
          derive the source code of the Service.
        </li>
        <li>
          Resell, sublicense, or redistribute access to the Service to any
          third party.
        </li>
        <li>
          Use the Service in a way that imposes an unreasonable or
          disproportionately large load on our infrastructure.
        </li>
        <li>
          Attempt to gain unauthorized access to any systems, networks, or
          data associated with the Service.
        </li>
        <li>
          Use automated scripts or bots against the Service without our prior
          written consent.
        </li>
      </ul>

      {/* AVAILABILITY */}
      <h2>Availability and service changes</h2>
      <p>
        The Service is provided on an <strong>"as is"</strong> and{" "}
        <strong>"as available"</strong> basis. We do not guarantee any specific
        level of uptime, availability, or service continuity. We may modify,
        suspend, or discontinue the Service (or any part of it) at any time,
        with or without notice, and we will not be liable to you or to any
        third party for doing so.
      </p>
      <p>
        Features may be added, changed, or removed over time. We will
        endeavor to provide reasonable notice of material changes where
        practicable, but are not obligated to do so.
      </p>

      {/* INTELLECTUAL PROPERTY */}
      <h2>Intellectual property</h2>
      <p>
        The {BRAND.name} application, including its source code, design,
        algorithms, and all associated materials, is the proprietary property
        of Legitimate LLC and is protected by copyright and other intellectual
        property laws.
      </p>
      <p>
        <strong>Your data belongs to you.</strong> All nutrition data you enter,
        all labels you generate, and all metaobjects written to your Shopify
        store are your property. We claim no ownership over your data or your
        generated label outputs.
      </p>
      <p>
        By using the Service, you grant Legitimate LLC a limited, non-exclusive
        license to process your data solely as necessary to provide the Service
        to you.
      </p>

      {/* THIRD-PARTY SERVICES */}
      <h2>Third-party services</h2>
      <p>
        {BRAND.name} is built on the Shopify platform and is subject to
        Shopify's own Terms of Service and Acceptable Use Policy. Your use of
        Shopify is governed by the agreements between you and Shopify. We are
        not responsible for Shopify's products, services, policies, or actions.
      </p>

      {/* LIMITATION OF LIABILITY */}
      <h2>Limitation of liability</h2>
      <p>
        To the maximum extent permitted by applicable law, Legitimate LLC and
        its officers, directors, employees, and agents shall not be liable for
        any indirect, incidental, special, consequential, punitive, or
        exemplary damages, including but not limited to:
      </p>
      <ul>
        <li>
          Loss of profits, revenue, business, data, or goodwill.
        </li>
        <li>
          Costs of procuring substitute goods or services.
        </li>
        <li>
          Any regulatory action, enforcement notice, recall, product
          withdrawal, fine, or penalty arising from reliance on or use of
          labels generated by the Service.
        </li>
        <li>
          Any consumer harm, personal injury, or property damage arising from
          labels generated by the Service.
        </li>
        <li>
          Business interruption or loss of use caused by unavailability of
          the Service.
        </li>
      </ul>
      <p>
        In any event, Legitimate LLC's total cumulative liability to you for
        any cause of action shall not exceed the total fees paid by you for
        the Service in the twelve (12) months preceding the claim. Because
        the Service is currently free, our aggregate liability is zero dollars
        ($0) unless applicable law requires otherwise.
      </p>
      <p>
        Some jurisdictions do not allow the exclusion or limitation of
        incidental or consequential damages. In those jurisdictions, the
        limitations above apply only to the extent permitted by law.
      </p>

      {/* DISCLAIMER OF WARRANTIES */}
      <h2>Disclaimer of warranties</h2>
      <p>
        To the maximum extent permitted by applicable law, the Service is
        provided without warranties of any kind, whether express or implied,
        including but not limited to implied warranties of merchantability,
        fitness for a particular purpose, title, and non-infringement. We do
        not warrant that the Service will be uninterrupted, error-free,
        secure, or that defects will be corrected. We do not warrant that the
        FDA rounding logic or label output will meet your specific regulatory
        requirements.
      </p>

      {/* INDEMNIFICATION */}
      <h2>Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless Legitimate LLC and
        its officers, directors, employees, and agents from and against any
        claims, liabilities, damages, losses, costs, and expenses (including
        reasonable attorneys' fees) arising out of or related to: (a) your use
        of the Service; (b) the accuracy of nutrition data you enter; (c) any
        label you print, publish, or use commercially; (d) your violation of
        these Terms; or (e) your violation of any applicable law or regulation.
      </p>

      {/* TERMINATION */}
      <h2>Termination</h2>
      <p>
        You may stop using the Service at any time by uninstalling{" "}
        {BRAND.name} from your Shopify store. Uninstalling the app will
        trigger deletion of your activity records from our database in
        accordance with our{" "}
        <a href="/privacy">
          Privacy Policy
        </a>
        . Label data stored as metaobjects on your store will remain on your
        store unless you delete them separately.
      </p>
      <p>
        We may suspend or terminate your access to the Service immediately,
        without prior notice or liability, if you breach any of these Terms or
        if we reasonably believe that continued access would harm the Service,
        other merchants, or third parties.
      </p>

      {/* GOVERNING LAW */}
      <h2>Governing law and dispute resolution</h2>
      <p>
        These Terms are governed by the laws of the State of Georgia, United
        States, without regard to conflict-of-law principles. Any dispute
        arising from or relating to these Terms or the Service shall be
        resolved in the state or federal courts located in Georgia, and you
        consent to the exclusive personal jurisdiction of those courts.
      </p>

      {/* CHANGES */}
      <h2>Changes to these terms</h2>
      <p>
        We reserve the right to update or modify these Terms at any time. When
        we do, we will update the "Last updated" date at the top of this page.
        Your continued use of the Service after a change constitutes your
        acceptance of the revised Terms. If you do not agree to the revised
        Terms, stop using the Service and uninstall the app.
      </p>
      <p>
        For material changes, we will make reasonable efforts to provide
        advance notice, but are not obligated to do so in all cases.
      </p>

      {/* CONTACT */}
      <h2>Contact</h2>
      <p>
        Questions about these Terms:{" "}
        <a href={`mailto:${BRAND.supportEmail}`}>
          {BRAND.supportEmail}
        </a>
        <br />
        Legitimate LLC — mailing address available on request.
      </p>
    </LegalPage>
  );
}
